import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatNumberThousands(value: number | string): string {
  const number = typeof value === 'string' ? parseFloat(value.replace(/[^\d.-]/g, '')) : value;
  if (isNaN(number)) return '';
  return new Intl.NumberFormat('vi-VN').format(number);
}

export function formatVND(value?: number | string | null): string {
  if (value === undefined || value === null || value === '') return '-';
  const number = typeof value === 'string' ? parseFloat(value.replace(/[^\d.-]/g, '')) : value;
  if (isNaN(number)) return '-';
  return `${new Intl.NumberFormat('vi-VN').format(number)} đ`;
}

/**
 * Normalizes Vietnamese text by removing diacritics and converting to uppercase
 * This is equivalent to the FullnameNormalize function from the backend
 */
export function fullnameNormalize(text: string): string {
  if (!text) return text;

  // Convert to Unicode Normalization Form D
  const normalizedString = text.normalize('NFD');
  let result = '';

  for (const char of normalizedString) {
    // Check if character is not a combining mark (diacritic)
    const code = char.codePointAt(0);
    if (code && !isCombiningMark(code)) {
      result += char;
    }
  }

  return result.trim().nonUnicode().normalize('NFC').toUpperCase();
}

/**
 * Helper function to check if a character is a combining mark (diacritic)
 */
function isCombiningMark(code: number): boolean {
  return (
    (code >= 0x0300 && code <= 0x036F) || // Combining Diacritical Marks
    (code >= 0x1AB0 && code <= 0x1AFF) || // Combining Diacritical Marks Extended
    (code >= 0x1DC0 && code <= 0x1DFF) || // Combining Diacritical Marks Supplement
    (code >= 0x20D0 && code <= 0x20FF) || // Combining Diacritical Marks for Symbols
    (code >= 0xFE20 && code <= 0xFE2F)    // Combining Half Marks
  );
}

/**
 * Converts Vietnamese text to non-Unicode (ASCII) equivalent
 * This is equivalent to the NonUnicode function from the backend
 */
function nonUnicode(str: string): string {
  str = str.toLowerCase();
  str = str.replace(/[àáạảãâầấậẩẫăằắặẳẵ]/g, 'a');
  str = str.replace(/[èéẹẻẽêềếệểễ]/g, 'e');
  str = str.replace(/[ìíịỉĩ]/g, 'i');
  str = str.replace(/[òóọỏõôồốộổỗơờớợởỡ]/g, 'o');
  str = str.replace(/[ùúụủũưừứựửữ]/g, 'u');
  str = str.replace(/[ỳýỵỷỹ]/g, 'y');
  str = str.replace(/đ/g, 'd');
  str = str.replace(/\s+/g, '-');
  str = str.replace(/,/g, '');
  str = str.replace(/\./g, '');
  return str;
}

// Extend String prototype to add nonUnicode method
declare global {
  interface String {
    nonUnicode(): string;
  }
}

String.prototype.nonUnicode = function () {
  return nonUnicode(String(this));
};